import { createContext, useContext, useEffect, useState } from 'react';
import { node, oneOf } from 'prop-types';
import { jssPreset, StylesProvider, ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { create } from 'jss';
import nested from 'jss-plugin-nested';

import AppCssBaseline from './AppCssBaseline';
import Themes from '../../themes/Themes';
import { useIsDarkMode } from '../../stores/SessionStore';
import useIsPrinting from '../../utilities/PrintUtilities';

const jss = create({
    plugins: [...jssPreset().plugins, nested()],
});

const ThemeOverrideContext = createContext();

export default function AppThemeProvider(props) {
    const { children } = props;

    // Using Dark theme as default for login pages.
    const [themeOverride, setThemeOverride] = useState();
    const [theme, setTheme] = useState(Themes.dark);
    const [isDarkMode] = useIsDarkMode();
    const isPrinting = useIsPrinting();

    // Wait until we have the user's services info before we load up the application
    useEffect(() => {
        if (isDarkMode !== undefined) {
            const lightOrDark = isDarkMode ? Themes.dark : Themes.light;

            setTheme(isPrinting ? Themes.print : lightOrDark);
        }
    }, [isDarkMode, isPrinting]);

    // Wait until we have loaded up the services before we paint
    if (!theme) {
        return null;
    }

    return (
        <ThemeOverrideContext.Provider value={setThemeOverride}>
            <StylesProvider jss={jss}>
                <ThemeProvider theme={Themes[themeOverride] || theme}>
                    <CssBaseline />
                    <AppCssBaseline />
                    {children}
                </ThemeProvider>
            </StylesProvider>
        </ThemeOverrideContext.Provider>
    );
}

AppThemeProvider.propTypes = {
    children: node,
};

AppThemeProvider.defaultProps = {
    children: null,
};

/**
 * This component forces a certain theme to be used. This is used in the login auth pages for force
 * dark mode. This does not have any effect while printing
 */
export function ThemeOverride({ children, theme }) {
    const setThemeOverride = useContext(ThemeOverrideContext);
    const isPrinting = useIsPrinting();

    useEffect(() => {
        if (isPrinting) {
            setThemeOverride(null);
            return;
        }

        setThemeOverride(theme);
        return () => setThemeOverride(null);
    }, [isPrinting, setThemeOverride, theme]);

    return children;
}

ThemeOverride.propTypes = {
    children: node,
    theme: oneOf(['dark', 'light']).isRequired,
};

ThemeOverride.defaultProps = {
    children: null,
};
